/* Ronini shared mobile interaction kit — see rn-swipe-row.css */
(() => {
  if (window.RNSwipeRow) { window.RNSwipeRow.init(document); return; }
  const mq = window.matchMedia('(max-width: 767px)');
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- swipe rows ---------- */
  function setupRow(row) {
    if (row.dataset.rnSwipeReady) return;
    row.dataset.rnSwipeReady = '1';
    row.classList.add('rn-swipe');
    const label = row.dataset.rnSwipe || 'Swipe to see more';
    /* region semantics + keyboard focus only while it is actually a swipe row (phones) */
    const a11y = () => {
      if (mq.matches) { row.tabIndex = 0; row.setAttribute('role', 'region'); row.setAttribute('aria-label', label); }
      else { row.removeAttribute('tabindex'); row.removeAttribute('role'); row.removeAttribute('aria-label'); }
    };

    let bar = row.dataset.rnSwipeProgress ? document.querySelector(row.dataset.rnSwipeProgress) : null;
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'rn-swipe-progress' + (row.hasAttribute('data-rn-swipe-light') ? ' rn-swipe-progress--light' : '');
      bar.setAttribute('aria-hidden', 'true');
      bar.appendChild(document.createElement('span'));
      row.insertAdjacentElement('afterend', bar);
    }
    const fill = bar.firstElementChild;

    const pad = () => {
      /* bleed the row to both viewport edges: padding = distance from each edge to the parent's content box */
      if (!mq.matches) { bar.style.marginLeft = bar.style.marginRight = ''; ['--rn-swipe-pad-l', '--rn-swipe-pad-r', '--rn-swipe-bleed-l', '--rn-swipe-bleed-r'].forEach((k) => row.style.removeProperty(k)); return; }
      const parent = row.parentElement;
      const r = parent.getBoundingClientRect();
      const cs = getComputedStyle(parent);
      const vw = document.documentElement.clientWidth;
      const d = (v) => Math.max(0, Math.min(40, Math.round(v)));
      const l = d(r.left + parseFloat(cs.paddingLeft) + parseFloat(cs.borderLeftWidth));
      const rr = d(vw - r.right + parseFloat(cs.paddingRight) + parseFloat(cs.borderRightWidth));
      /* margin pulls the row out to the viewport edge; padding keeps at least 16px of gutter */
      row.style.setProperty('--rn-swipe-bleed-l', l + 'px');
      row.style.setProperty('--rn-swipe-bleed-r', rr + 'px');
      row.style.setProperty('--rn-swipe-pad-l', Math.max(16, l) + 'px');
      row.style.setProperty('--rn-swipe-pad-r', Math.max(16, rr) + 'px');
      /* keep the progress bar inside the same gutter as the cards */
      bar.style.marginLeft = Math.max(0, 16 - l) + 'px';
      bar.style.marginRight = Math.max(0, 16 - rr) + 'px';
    };
    let queued = false;
    const update = () => {
      queued = false;
      const max = row.scrollWidth - row.clientWidth;
      const w = Math.max(8, Math.min(100, row.scrollWidth ? (row.clientWidth / row.scrollWidth) * 100 : 100));
      const p = max > 0 ? row.scrollLeft / max : 0;
      fill.style.setProperty('--rn-swipe-w', w + '%');
      fill.style.setProperty('--rn-swipe-x', (p * (100 / w - 1) * 100) + '%');
      bar.style.visibility = max > 2 ? '' : 'hidden';
    };
    const request = () => { if (!queued) { queued = true; requestAnimationFrame(update); } };
    row.addEventListener('scroll', request, { passive: true });
    const onResize = () => { a11y(); pad(); request(); };
    window.addEventListener('resize', onResize, { passive: true });
    if (mq.addEventListener) mq.addEventListener('change', onResize);
    row.addEventListener('keydown', (e) => {
      if (!mq.matches || (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') || e.target !== row) return;
      const card = row.firstElementChild;
      const step = card ? card.getBoundingClientRect().width + 12 : row.clientWidth;
      row.scrollBy({ left: e.key === 'ArrowRight' ? step : -step, behavior: reduce.matches ? 'auto' : 'smooth' });
      e.preventDefault();
    });
    onResize();
  }

  /* ---------- tab strips ---------- */
  function keepInView(strip, tab, instant) {
    if (!strip || !tab || strip.scrollWidth <= strip.clientWidth + 1) return;
    const s = strip.getBoundingClientRect();
    const t = tab.getBoundingClientRect();
    const target = strip.scrollLeft + (t.left - s.left) - (s.width - t.width) / 2;
    strip.scrollTo({ left: Math.max(0, target), behavior: instant || reduce.matches ? 'auto' : 'smooth' });
  }
  function setupStrip(strip) {
    if (strip.dataset.rnTabstripReady) return;
    strip.dataset.rnTabstripReady = '1';
    const fades = () => {
      const max = strip.scrollWidth - strip.clientWidth;
      strip.classList.toggle('is-fade-start', strip.scrollLeft > 4);
      strip.classList.toggle('is-fade-end', max - strip.scrollLeft > 4);
    };
    strip.addEventListener('scroll', fades, { passive: true });
    window.addEventListener('resize', fades, { passive: true });
    const tabs = () => Array.from(strip.querySelectorAll('[role="tab"]'));
    const active = () => tabs().find((t) => t.getAttribute('aria-selected') === 'true') || tabs()[0];
    /* keep the active tab in view whenever the selection changes (click, keys, swipe, editor) */
    new MutationObserver(() => keepInView(strip, active())).observe(strip, { subtree: true, attributes: true, attributeFilter: ['aria-selected'] });
    if (strip.hasAttribute('data-rn-tabs-keys')) {
      strip.addEventListener('keydown', (e) => {
        const list = tabs();
        const i = list.indexOf(e.target.closest('[role="tab"]'));
        if (i < 0) return;
        let n = -1;
        if (e.key === 'ArrowRight') n = (i + 1) % list.length;
        else if (e.key === 'ArrowLeft') n = (i - 1 + list.length) % list.length;
        else if (e.key === 'Home') n = 0;
        else if (e.key === 'End') n = list.length - 1;
        if (n < 0) return;
        e.preventDefault();
        list[n].click();
        list[n].focus({ preventScroll: true });
      });
    }
    fades();
    requestAnimationFrame(() => { keepInView(strip, active(), true); fades(); });
  }

  /* ---------- swipeable panels ---------- */
  function setupPanels(box) {
    if (box.dataset.rnTabSwipeReady) return;
    box.dataset.rnTabSwipeReady = '1';
    const strip = box.dataset.rnTabSwipe ? box.ownerDocument.querySelector(box.dataset.rnTabSwipe) : null;
    const tabsOf = () => Array.from((strip || box).querySelectorAll('[role="tab"]'));
    let x0 = 0, y0 = 0, tracking = false;
    box.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1 || (e.target.closest && e.target.closest('[data-rn-tabstrip], [data-rn-swipe], input, textarea, select'))) { tracking = false; return; }
      tracking = true; x0 = e.touches[0].clientX; y0 = e.touches[0].clientY;
    }, { passive: true });
    box.addEventListener('touchend', (e) => {
      if (!tracking) return; tracking = false;
      const t = e.changedTouches[0];
      const dx = t.clientX - x0, dy = t.clientY - y0;
      if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      const list = tabsOf();
      const i = list.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      const n = i + (dx < 0 ? 1 : -1);
      if (i < 0 || n < 0 || n >= list.length) return;
      box.dataset.dir = dx < 0 ? 'next' : 'prev';
      list[n].click();
    }, { passive: true });
    /* direction for click/keyboard changes */
    (strip || box).addEventListener('click', (e) => {
      const tab = e.target.closest && e.target.closest('[role="tab"]');
      if (!tab || !e.isTrusted) return;
      const list = tabsOf();
      const cur = list.findIndex((t) => t.getAttribute('aria-selected') === 'true');
      box.dataset.dir = list.indexOf(tab) < cur ? 'prev' : 'next';
    }, true);
  }

  function init(root) {
    (root || document).querySelectorAll('[data-rn-swipe]').forEach(setupRow);
    (root || document).querySelectorAll('[data-rn-tabstrip]').forEach(setupStrip);
    (root || document).querySelectorAll('[data-rn-tab-swipe]').forEach(setupPanels);
  }
  window.RNSwipeRow = { init, keepInView };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
